import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import HighlightIcon from '@mui/icons-material/Highlight'
import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { yellow } from '@mui/material/colors'
import { useContext, useEffect, useRef, useState } from 'react'
import AppContext from '../../../context/appContext'
import { useExclusionAreaTypes } from '../../../hooks/data/useExclusionAreaTypes'
import { dataLayersAreas, dataLayersPoints } from '../../../utils/Files/dataLayers'
import { LAND_PLAN_YEAR } from '../../../utils/constants'
import {
  OTHER_EXCLUSION_AREA_TYPE_ID,
  resolveHydratedExclusionAreaType,
  resolveSelectedExclusionAreaTypeId,
  toTypeId,
} from './exclusionAreaTypeResolution'
import { getFeatureDisplayName } from './getFeatureDisplayName'

const defaultFeatureName = 'Área sin nombre'

const AreaRow = ({
  year,
  feature,
  handleFeature,
  type,
  saved,
  handleName,
  handleExclusionAreaTypeChange = null,
  handleOtherName = null,
  handleHasGrazingManagement = null,
  handleSelectFeature = null,
  handleRemoveFeature = null,
  error = false,
}) => {
  const [featureName, setFeatureName] = useState(defaultFeatureName)
  const [otherName, setOtherName] = useState('')
  const [fgChose, setFgChose] = useState('')
  const [selectedType, setSelectedType] = useState('')
  const [loading, setLoading] = useState(false)
  const dataLayerOptions =
    feature?.geometry?.type === 'Polygon' || feature?.geometry?.type === 'MultiPolygon'
      ? dataLayersAreas
      : dataLayersPoints
  const [isMonitoringSite, setIsMonitoringSite] = useState(false)
  const { currentFarm } = useContext(AppContext)
  const { data: exclusionAreaTypes } = useExclusionAreaTypes({ programId: currentFarm?.programId })
  const hydratedExclusionType = useRef(false)
  // Refs, not state: ShowFolders passes fresh handlers and a fresh `feature` on every render, so a
  // state flag would let the sync effects re-run and clobber what the user is still typing.
  const nameChanged = useRef(false)
  const otherNameChanged = useRef(false)
  const exclusionTypeLabelId = `exclusion-category-label-${feature?.properties?.id}`
  const featureGroupLabelId = `feature-group-label-${feature?.properties?.id}`

  const showsOtherNameField = selectedType === OTHER_EXCLUSION_AREA_TYPE_ID

  const pointLayers = ['unassignedPoints', 'otherSites']

  const handleChangeFeatureGroup = event => {
    setFgChose(event.target.value)
    handleFeature(feature, type, event.target.value)
    setLoading(false)
  }

  const handleTemporalFeatureNameChange = event => {
    nameChanged.current = true
    setFeatureName(event.target.value)
  }

  const handleFeatureNameChange = () => {
    if (nameChanged.current) {
      handleName(feature, type, featureName)
      nameChanged.current = false
    }
  }

  const handleTemporalOtherNameChange = event => {
    otherNameChanged.current = true
    setOtherName(event.target.value)
  }

  const handleOtherNameChange = () => {
    if (otherNameChanged.current) {
      handleOtherName(feature, otherName)
      otherNameChanged.current = false
    }
  }

  const handleTypeChange = event => {
    const id = resolveSelectedExclusionAreaTypeId(event.target.value, exclusionAreaTypes)
    handleExclusionAreaTypeChange(feature, id)
    setSelectedType(id)
  }

  const handleIsMonitoringSiteChange = () => {
    if (!isMonitoringSite) {
      handleFeature(feature, type, 'monitoringSites')
    } else {
      handleFeature(feature, type, 'unassignedPoints')
    }

    setLoading(false)
  }

  useEffect(() => {
    if (type) {
      setFgChose(type)
      if (type === 'monitoringSites') {
        setIsMonitoringSite(true)
      }
    }
    if (nameChanged.current) return

    // While the dedicated "Otro nombre" field owns `otherName`, the display name would reseed
    // "Nombre" with that previous value and silently revert the rename the user just made.
    const name = showsOtherNameField
      ? feature?.properties?.name
      : getFeatureDisplayName(feature?.properties)

    if (name) {
      setFeatureName(name)
    } else {
      handleName(feature, type, defaultFeatureName)
    }
  }, [feature, type, handleName, showsOtherNameField])

  useEffect(() => {
    if (type !== 'exclusion' || !exclusionAreaTypes || hydratedExclusionType.current) return
    hydratedExclusionType.current = true

    const { typeId, selectedTypeId } = resolveHydratedExclusionAreaType(
      feature?.properties,
      exclusionAreaTypes,
    )

    if (typeId !== null) {
      handleExclusionAreaTypeChange(feature, typeId)
      setSelectedType(selectedTypeId)
    }
  }, [exclusionAreaTypes, feature, type, handleExclusionAreaTypeChange])

  useEffect(() => {
    if (otherNameChanged.current) return

    if (feature?.properties?.otherName) {
      setOtherName(feature?.properties?.otherName)
    } else {
      setOtherName(feature?.properties?.name ?? '')
    }
  }, [feature])

  return (
    feature &&
    feature.properties &&
    feature.geometry && (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            marginTop: 2,
            marginBottom: 1,
          }}
        >
          <Grid container alignItems="center" spacing={1}>
            {pointLayers.includes(fgChose) ? (
              <Grid item sx={{ textAlign: 'center', pb: 1 }} xs={12}>
                <Typography color={error ? 'error' : ''} fontWeight="bold" variant="body2">
                  {pointLayers.includes(fgChose) ? 'Sitio - ' : ''}
                  {feature.properties.otherName
                    ? feature.properties.otherName
                    : feature.properties.name}
                </Typography>
              </Grid>
            ) : null}
            {fgChose === 'exclusion' ? (
              <>
                <Grid item lg={3} md={6} xl={4} xs={6}>
                  <FormControl fullWidth>
                    <InputLabel id={exclusionTypeLabelId} size="small">
                      Tipo de área de exclusión
                    </InputLabel>
                    <Select
                      disabled={saved}
                      error={selectedType === ''}
                      id={`exclusion-category-select-${feature.properties.id}`}
                      label="Tipo de área de exclusión"
                      labelId={exclusionTypeLabelId}
                      size="small"
                      value={selectedType}
                      onChange={handleTypeChange}
                    >
                      {exclusionAreaTypes?.map(areaType => {
                        const areaTypeId = toTypeId(areaType.id)
                        return (
                          <MenuItem
                            key={areaType.id}
                            disabled={areaTypeId === OTHER_EXCLUSION_AREA_TYPE_ID}
                            size="small"
                            value={areaTypeId}
                          >
                            {areaType?.es_AR}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item lg={3} md={6} xl={4} xs={6}>
                  <FormControl fullWidth component="fieldset">
                    <Box alignItems="center" display="flex" flexDirection="column">
                      <Checkbox
                        checked={feature.properties.hasGrazingManagement === true}
                        disabled={saved}
                        name="hasGrazingManagement"
                        onChange={e => {
                          handleHasGrazingManagement(feature, e.target.checked)
                        }}
                      />
                      <FormLabel component="legend" style={{ textAlign: 'center', width: '100%' }}>
                        Con pastoreo
                      </FormLabel>
                    </Box>
                  </FormControl>
                </Grid>
              </>
            ) : null}
            {!pointLayers.includes(type) ? (
              <Grid item lg={5} md={6} xl={5} xs={6}>
                {feature?.geometry?.type === 'Point' && fgChose !== 'monitoringSites' ? (
                  <Typography
                    size="small"
                    sx={{
                      margin: 2,
                    }}
                  >
                    {feature?.properties?.name ?? 'Punto sin nombre'}
                  </Typography>
                ) : null}
                {feature?.geometry?.type === 'Polygon' ||
                feature?.geometry?.type === 'MultiPolygon' ||
                (feature?.geometry?.type === 'Point' && fgChose === 'monitoringSites') ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <TextField
                      disabled={saved}
                      error={error}
                      label="Nombre"
                      size="small"
                      value={featureName}
                      variant="outlined"
                      onBlur={handleFeatureNameChange}
                      onChange={handleTemporalFeatureNameChange}
                    />
                    {loading ? (
                      <LinearProgress
                        sx={{
                          width: '100%',
                          height: 5,
                        }}
                      />
                    ) : null}
                  </Box>
                ) : null}
              </Grid>
            ) : null}
            {!saved && (
              <Grid item lg={3} md={6} xl={5} xs={6}>
                <FormControl fullWidth>
                  <InputLabel id={featureGroupLabelId} size="small">
                    Categoría
                  </InputLabel>
                  <Select
                    disabled={saved || isMonitoringSite}
                    label="Categoría"
                    labelId={featureGroupLabelId}
                    placeholder="Seleccionar"
                    size="small"
                    value={fgChose}
                    onChange={handleChangeFeatureGroup}
                  >
                    {dataLayerOptions.map(layer => {
                      return (
                        <MenuItem
                          key={layer.farmLayerType}
                          disabled={layer.farmLayerType === 'monitoringSites'}
                          size="small"
                          value={layer.farmLayerType}
                        >
                          {layer.label}
                        </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {feature?.geometry?.type === 'Point' ? (
              <Grid item lg={5} md={6} xl={5} xs={6}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    size="small"
                    sx={{
                      mx: { xs: 0, xl: 2 },
                    }}
                  >
                    Sitio de monitoreo
                  </Typography>
                  <Checkbox
                    checked={isMonitoringSite}
                    disabled={saved}
                    onChange={handleIsMonitoringSiteChange}
                  />
                </Box>
              </Grid>
            ) : null}
            {showsOtherNameField ? (
              <Grid item lg={5} md={6} xl={5} xs={6}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <TextField
                    disabled={saved}
                    label="Otro nombre"
                    size="small"
                    value={otherName}
                    variant="outlined"
                    onBlur={handleOtherNameChange}
                    onChange={handleTemporalOtherNameChange}
                  />
                </Box>
              </Grid>
            ) : null}
            {feature?.properties?.area ? (
              <Grid item lg={3} md={12} sx={{ textAlign: 'center' }} xl={2} xs={12}>
                <Typography variant="body">
                  {typeof feature?.properties?.area === 'number'
                    ? `${feature?.properties?.area.toFixed(2)} ha`
                    : feature?.properties?.area}
                </Typography>
              </Grid>
            ) : null}
            {saved &&
              type === 'paddocks' &&
              // TODO: REMOVE TEMPLATE AFTER THE ONGOING FARMS ARE PROCESSED
              (year === LAND_PLAN_YEAR || year === 'TEMPLATE') && (
                <Grid item lg={5} md={6} sx={{ textAlign: 'center' }} xl={5} xs={6}>
                  <Checkbox disabled checked={feature.properties.isInProject} size="small" />
                  <FormLabel component="legend" style={{ textAlign: 'center', width: '100%' }}>
                    En proyecto
                  </FormLabel>
                </Grid>
              )}
          </Grid>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignContent: 'center',
            alignItems: 'center',
            marginLeft: 1,
          }}
        >
          <IconButton onClick={() => handleSelectFeature(feature)}>
            <HighlightIcon
              sx={{ color: feature.properties.selected ? yellow[700] : 'primary.main' }}
            />
          </IconButton>
          {handleRemoveFeature && (
            <DeleteOutlineIcon
              sx={{
                cursor: 'pointer',
                color: 'red',
              }}
              onClick={() => handleRemoveFeature(feature, type)}
            />
          )}
        </Box>
      </Box>
    )
  )
}

export default AreaRow
